import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAssets } from "../../assets/contexts/AssetsContext";
import Button from "../../common/components/Button";
import { toast } from "../../common/components/Toast";
import { confirmColumnMappings, getExpectedFields } from "../api/files";

const ColumnMappingModal = ({
  open,
  onClose,
  fileData,
  onMappingConfirmed,
}) => {
  const { t } = useTranslation("sources");
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [mappings, setMappings] = useState({});
  const [expectedFields, setExpectedFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sourceLabel, setSourceLabel] = useState("");
  const [defaultPeriodStart, setDefaultPeriodStart] = useState("");
  const [defaultPeriodEnd, setDefaultPeriodEnd] = useState("");
  const { refetch: refetchAssets } = useAssets();

  useEffect(() => {
    if (open && fileData) {
      // Initialize mappings with suggested mappings
      setMappings(fileData.suggested_mappings || {});
      setSourceLabel(fileData.file.source_label || "");
      setDefaultPeriodStart(fileData.file.default_period_start || "");
      setDefaultPeriodEnd(fileData.file.default_period_end || "");

      // Fetch expected fields
      fetchExpectedFields();
    }
  }, [open, fileData]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchExpectedFields = async () => {
    try {
      const response = await getExpectedFields();
      setExpectedFields(response.fields);
    } catch (error) {
      console.error("Error fetching expected fields:", error);
      toast.error(t("manualImport.columnMapping.errors.failedToLoad"));
    }
  };

  const handleMappingChange = (expectedField, csvColumn) => {
    setMappings((prev) => ({
      ...prev,
      [expectedField]: csvColumn === "" ? null : csvColumn,
    }));
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const response = await confirmColumnMappings(
        fileData.file.id,
        mappings,
        sourceLabel,
        defaultPeriodStart,
        defaultPeriodEnd,
      );
      toast.success(response.report.message);
      onMappingConfirmed(response);
      await refetchAssets();

      onClose();
    } catch (error) {
      // Handle validation errors with more detailed messaging
      const errorMessage = error.message;
      if (errorMessage.includes(";")) {
        // Multiple validation errors - show them as a list
        const errors = errorMessage.split(";").map((err) => err.trim());
        toast.error(
          <div>
            <strong>
              {t("manualImport.columnMapping.errors.validationErrors")}
            </strong>
            <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
              {errors.map((err, index) => (
                <li key={index}>{err}</li>
              ))}
            </ul>
          </div>,
          { autoClose: 8000 },
        );
      } else {
        toast.error("Error: " + errorMessage, { autoClose: 6000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const getUsedColumns = () => {
    return Object.values(mappings).filter((col) => col !== null && col !== "");
  };

  const isColumnUsed = (column, currentField) => {
    const usedColumns = getUsedColumns();
    return usedColumns.includes(column) && mappings[currentField] !== column;
  };

  const getRequiredFields = () => {
    // Only title is truly required — all other fields are optional
    // since sales data varies widely across platforms
    return new Set(["title"]);
  };

  const isFieldRequired = (fieldKey) => {
    return getRequiredFields().has(fieldKey);
  };

  const getFieldRequirementType = (fieldKey) => {
    if (fieldKey === "title") {
      return "required";
    }
    return null;
  };

  const getRequirementChip = (fieldKey) => {
    if (getFieldRequirementType(fieldKey) === "required") {
      return (
        <Chip
          label={t("manualImport.columnMapping.badges.required")}
          size="small"
          color="error"
          variant="outlined"
        />
      );
    }
    return null;
  };

  if (!fileData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { minHeight: isMobile ? 0 : "70vh" },
      }}
    >
      <DialogTitle>
        {t("manualImport.columnMapping.title")}
        <Typography variant="bodySm" color="text.secondary" sx={{ mt: 1 }}>
          {t("manualImport.columnMapping.fileLabel")} {fileData.file.name}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("manualImport.columnMapping.instructions")}
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t("manualImport.columnMapping.platformLabelTitle")}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label={t("manualImport.columnMapping.platformLabelInput")}
            placeholder={t(
              "manualImport.columnMapping.platformLabelPlaceholder",
            )}
            value={sourceLabel}
            onChange={(e) => setSourceLabel(e.target.value)}
            helperText={t("manualImport.columnMapping.platformLabelHelper")}
            sx={{ mb: 1 }}
          />
        </Box>

        {!mappings.period_start && !mappings.period_end && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t("manualImport.columnMapping.defaultDatesTitle")}
            </Typography>
            <Typography variant="bodySm" color="text.secondary" sx={{ mb: 2 }}>
              {t("manualImport.columnMapping.defaultDatesHelper")}
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                type="date"
                size="small"
                label={t("manualImport.columnMapping.periodStart")}
                value={defaultPeriodStart}
                onChange={(e) => setDefaultPeriodStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                type="date"
                size="small"
                label={t("manualImport.columnMapping.periodEnd")}
                value={defaultPeriodEnd}
                onChange={(e) => setDefaultPeriodEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t("manualImport.columnMapping.columnMappingsTitle")}
          </Typography>

          <TableContainer variant="outlined" sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>
                      {t("manualImport.columnMapping.table.expectedField")}
                    </strong>
                  </TableCell>
                  <TableCell>
                    <strong>
                      {t("manualImport.columnMapping.table.csvColumn")}
                    </strong>
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    <strong>
                      {t("manualImport.columnMapping.table.description")}
                    </strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expectedFields.map((field) => (
                  <TableRow
                    key={field.key}
                    sx={{
                      backgroundColor:
                        isFieldRequired(field.key) && !mappings[field.key]
                          ? "rgba(255, 152, 0, 0.08)"
                          : "inherit",
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="bodySm" fontWeight="medium">
                          {field.label}
                        </Typography>
                        {getRequirementChip(field.key)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FormControl
                        fullWidth
                        size="small"
                        error={
                          isFieldRequired(field.key) && !mappings[field.key]
                        }
                      >
                        <InputLabel>
                          {t("manualImport.columnMapping.selectColumn")}
                        </InputLabel>
                        <Select
                          value={mappings[field.key] || ""}
                          onChange={(e) =>
                            handleMappingChange(field.key, e.target.value)
                          }
                          label={t("manualImport.columnMapping.selectColumn")}
                        >
                          <MenuItem value="">
                            <em>{t("manualImport.columnMapping.none")}</em>
                          </MenuItem>
                          {fileData.csv_headers.map((header) => (
                            <MenuItem
                              key={header}
                              value={header}
                              disabled={isColumnUsed(header, field.key)}
                            >
                              {header}
                              {isColumnUsed(header, field.key) && (
                                <Chip
                                  label={t(
                                    "manualImport.columnMapping.badges.used",
                                  )}
                                  size="small"
                                  color="warning"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", sm: "table-cell" } }}
                    >
                      <Typography variant="bodySm" color="text.secondary">
                        {field.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {fileData.csv_preview && fileData.csv_preview.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t("manualImport.columnMapping.csvPreviewTitle")}
            </Typography>
            <TableContainer variant="outlined" sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {fileData.csv_headers.map((header) => (
                      <TableCell key={header}>
                        <strong>{header}</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fileData.csv_preview.map((row, index) => (
                    <TableRow key={index}>
                      {fileData.csv_headers.map((header) => (
                        <TableCell key={header}>{row[header] || "-"}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("manualImport.columnMapping.buttons.cancel")}
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={loading}>
          {loading
            ? t("manualImport.columnMapping.buttons.processing")
            : t("manualImport.columnMapping.buttons.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnMappingModal;
